/** Open a new work order. Status changes, vendor assignment, and notes all happen from WorkOrderShow. */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm, useList } from '@refinedev/core';
import { WORK_ORDER_PRIORITIES } from 'shared';
import {
  Field,
  FormErrors,
  Select,
  TextArea,
  TextInput,
  fieldErrors,
} from '../../../components/Form.js';

interface UnitOption {
  id: string;
  label: string;
  propertyName?: string;
}

interface WorkOrderRecord {
  id: string;
}

export function WorkOrderForm() {
  const navigate = useNavigate();

  const [unitId, setUnitId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [scheduledOn, setScheduledOn] = useState('');

  const { data: unitsData, isLoading: unitsLoading } = useList<UnitOption>({
    resource: 'units',
    pagination: { current: 1, pageSize: 200 },
  });

  const { onFinish, mutation } = useForm<WorkOrderRecord>({
    resource: 'work-orders',
    action: 'create',
    redirect: false,
  });

  const units = unitsData?.data ?? [];
  const errors = fieldErrors(mutation.error);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    const payload = {
      unitId,
      title,
      description: description || null,
      priority,
      scheduledOn: scheduledOn || null,
    };

    const saved = (await onFinish(payload)) as { data?: WorkOrderRecord } | undefined;
    if (saved?.data?.id) void navigate(`/staff/work-orders/${saved.data.id}`);
  }

  return (
    <section>
      <div className="row">
        <h1>New work order</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/work-orders')}
        >
          ← All work orders
        </button>
      </div>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={mutation.error} />

        <Field label="Unit" name="unitId" required error={errors.unitId}>
          <select
            id="unitId"
            name="unitId"
            className="field__input"
            value={unitId}
            onChange={(event) => setUnitId(event.target.value)}
            required
            disabled={unitsLoading}
          >
            <option value="">{unitsLoading ? 'Loading units…' : 'Select a unit'}</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label} · {unit.propertyName ?? unit.id}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Title" name="title" required error={errors.title}>
          <TextInput
            name="title"
            value={title}
            onChange={setTitle}
            placeholder="Leaking kitchen faucet"
            required
          />
        </Field>

        <Field label="Description" name="description" error={errors.description}>
          <TextArea name="description" value={description} onChange={setDescription} />
        </Field>

        <Field label="Priority" name="priority" required error={errors.priority}>
          <Select
            name="priority"
            value={priority}
            onChange={setPriority}
            options={WORK_ORDER_PRIORITIES}
          />
        </Field>

        <Field label="Scheduled" name="scheduledOn" error={errors.scheduledOn}>
          <TextInput name="scheduledOn" type="date" value={scheduledOn} onChange={setScheduledOn} />
        </Field>

        <div className="row">
          <button
            type="submit"
            className="button"
            disabled={mutation.isLoading || !unitId || !title}
          >
            {mutation.isLoading ? 'Saving…' : 'Open work order'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void navigate('/staff/work-orders')}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
